
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  shallowRef,
  ref,
  toRef,
  watch,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import { ITabConfig } from "@/interfaces/iTabConfig";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { FormatMethods } from "@/helpers/data-format";
import * as Yup from "yup";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import {
  locationSiteTableHeaders,
  locationSiteTableBulkDelete,
} from "../dataTableOptions";
import { ISite } from "@/interfaces/iSite";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { useToast } from "vue-toastification";
import { FORM_TABS } from "@/views/form/dataTableOptions";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import { hasPermission } from "@/core/services/permission.service";
import { LocationAPI } from "@/services/api/Location.service";
import { ISiteLocation } from "@/interfaces/iLocation";
import { getLookups } from "@/services/api/Lookup.service";
import { siteLocationTableBulkDelete } from "@/views/site/siteDataTableOptions";
import SiteDetailModal from "@/views/site/modal/SiteDetailModal.vue";
import { debounce } from "lodash";

export default defineComponent({
  props: {
    data: { type: Object as PropType<ISite>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: { Datatable, VZDeleteModal, PreviewLink, SiteDetailModal },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const options = ref<{ label: string; value: string }[]>([]);
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const showDeleteDialog = ref<any>(false);
    const showSearch = true;
    const showDetailDialog = ref<any>(false);
    const tableData = ref<ISiteLocation[]>([]);
    const allTableData = ref<ISiteLocation[]>([]);
    const selectedForAction = ref<Partial<ISiteLocation>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(siteLocationTableBulkDelete());
    const tableHeader = ref(locationSiteTableHeaders());
    // const tableFilter = ref(siteFormsTableFilters());
    const loadingSiteDetailModal = ref(false);
    const selectedSite = ref<Partial<ISite>>({});
    const Datatable = ref();
    const format = computed(() => {
      return FormatMethods;
    });
    const tabs = shallowRef<ITabConfig[]>(FORM_TABS());
    const viewName = "locationSites";
    const toast = useToast();
    const message = t("pages.location.locationSiteDeleted");
    const formAddedMsg = t("pages.location.locationSite");
    let formData = ref<IAddEditFormModel>({
      page: "Site Location",
      header: "Site Location",
      edit: false,
      data: null,
      validationSchema: Yup.object({
        siteId: Yup.array()
          .nullable()
          .min(1, t("common.site") + " " + t("common.isRequired"))
          .required(t("common.site") + " " + t("common.isRequired")),
      }),
      label: [{ addHeader: t("pages.site.assignSite") }],
      model: [
        {
          name: "siteId",
          label: t("common.site"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          options: options.value,
          multiSelect: true,
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:BUSINESS:SITE:CREATE",
      delete: "VYZOR:BUSINESS:SITE:DELETE",
      update: "VYZOR:BUSINESS:SITE:UPDATE",
    });
    onMounted(async () => {
      try {
        // await queryUpdate(dataProp.value.webId, {
        //   page: page.value - 1,
        //   size: rowsPerPage.value,
        // });
        loading.value = true;
        loadLookupOptions();
      } catch (err) {
        console.log("error in getting location list: ", err);
      }
    });

    const loadLookupOptions = async () => {
      await getLookups("site/filter/SITES")
        .then((res) => {
          options.value = [];
          res.map((data) => {
            options.value.push({
              label: data.text,
              value: data.value,
            });
          });
          const siteIds = allTableData.value.map((item) => Number(item.siteId));
          const filteredOptions = options.value.filter(
            (option) => !siteIds.includes(Number(option.value))
          );
          formData.value.model[0].options = filteredOptions;
        })
        .catch((err) => {
          console.error(err);
        });
    };

    const submit = async (reqData) => {
      loading.value = true;
      await LocationAPI.getInstance()
        .addSiteLocations(
          { siteIds: reqData.siteId, locationId: dataProp.value.webId },
          formData.value.edit
        )
        .then(() => {
          toast.success(formAddedMsg);
          queryUpdate(dataProp.value.webId, { ...localPreference.value });
        })
        .catch((err) => {
          console.log("error in assigning form: ", err);
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(dataProp.value.webId, params);
    };

    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data = await LocationAPI.getInstance().siteLocations({
          ...params,
          searchSite: params.search,
          locationId: dataProp.value.webId,
        });
        const allData = await LocationAPI.getInstance().siteLocations({
          status: "ACTIVE",
          size: 10000,
          locationId: dataProp.value.webId,
        });
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        allTableData.value = [...allData.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loadLookupOptions();
        loading.value = false;
      }
    };
    const onBulkDelete = async (ids) => {
      showDeleteDialog.value = false;
      await LocationAPI.getInstance()
        .deleteSiteLocation(ids)
        .then(() => {
          toast.success(message);
          refreshList();
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };

    const openDetailModal = async (form) => {
      showDetailDialog.value = true;
      try {
        loadingSiteDetailModal.value = true;
        selectedSite.value = {};
        const data = await LocationAPI.getInstance().getSiteById(form.siteId);

        selectedSite.value = data;
      } finally {
        loadingSiteDetailModal.value = false;
      }
    };
    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;

    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        tableData.value = [];
        lastFetchedWebId = webId;
        await queryUpdate(+webId, {
          page: page.value - 1,
          size: INNER_PAGE_ROWS_PER_PAGE,
        });
      }
    }, 300);

    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          // await queryUpdate(dataProp.value.webId, {
          //   page: page.value - 1,
          //   size: rowsPerPage.value,
          // });
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
          allTableData.value = [];
        }
      }
    );
    return {
      Datatable,
      bulkDelete,
      format,
      tableData,
      tableHeader,
      // tableFilter,
      showSearch,
      page,
      tabs,
      total,
      rowsPerPage,
      loading,
      selectedForAction,
      syncQuery,
      formData,
      submit,
      refreshList,
      viewName,
      onBulkDelete,
      openDetailModal,
      permissionModel,
      hasPermission,
      t,
      loadingSiteDetailModal,
      selectedSite,
      showDeleteDialog,
      showDetailDialog,
      loadLookupOptions,
    };
  },
});
